import { useQuery } from '@tanstack/react-query';
import {
  AdminHeader,
  AdminSpinner,
  Button,
  deliveriesImage,
  Div,
  getIncidentMessages,
  H3,
  Img,
  IncidentMessage,
  ModalPortal,
  P,
  queryKeys,
  ResponseType,
} from '@vgrubs/components';
import { useState } from 'react';
import styled from 'styled-components';
import { DeleteIncidentMessageModal } from './DeleteIncidentMessageModal';
import { IncidentMessageCard } from './IncidentMessageCard';
import { IncidentMessageModal } from './IncidentMessageModal';
import {
  freeStatuses,
  isEtaMessage,
  isFoodRatingMessage,
  sortMessages,
} from './incidentMessageRules';

export const IncidentMessages = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteIncidentMessage, setDeleteIncidentMessage] =
    useState<IncidentMessage | null>(null);
  const [editIncidentMessage, setEditIncidentMessage] =
    useState<IncidentMessage | null>(null);
  const { data: messages, isLoading } = useQuery<
    ResponseType<IncidentMessage[]>
  >({
    queryKey: queryKeys.incidentMessages.all,
    queryFn: getIncidentMessages,
  });

  const allMessages = messages?.data ?? [];
  // Room is left while a status has no message yet, or the running-late or food-rating one is
  // missing.
  const isAddNewMessageAllowed =
    !!messages &&
    (freeStatuses(allMessages).length > 0 ||
      !allMessages.some(isEtaMessage) ||
      !allMessages.some(isFoodRatingMessage));
  const sortedMessages = sortMessages(allMessages);

  return (
    <>
      {isLoading && <AdminSpinner />}
      <AdminHeader
        title="vDrive Tracking Link"
        children={
          <Div $mobile>
            {isAddNewMessageAllowed && (
              <Button $selected onClick={() => setModalIsOpen(true)}>
                Add Message
              </Button>
            )}
          </Div>
        }
      />
      <Div $m="32px 0" $mSM="20px 0" $flex $alignCenter $between>
        <Div>
          <H3 $m="0">Automatic Messages</H3>
          <P $m="0">
            Customize the automatic text messages for vDrive order updates sent
            to customers.
          </P>
        </Div>
        <Div $desktop>
          {isAddNewMessageAllowed && (
            <Button $selected onClick={() => setModalIsOpen(true)}>
              Add Message
            </Button>
          )}
        </Div>
      </Div>
      {!isLoading && messages && messages.data.length < 1 ? (
        <Div $flex $column $alignCenter $mt="150">
          <Img alt="" $width="200px" src={deliveriesImage} $mb="24" />
          <H3 $mb="24" $center>
            No Incident Messages
          </H3>
          <P $mb="32" $center>
            No incident messages found.
          </P>
        </Div>
      ) : (
        <MessagesContainer>
          {sortedMessages.map((message) => (
            <IncidentMessageCard
              key={message.id}
              message={message}
              onEdit={() => setEditIncidentMessage(message)}
              onDelete={() => setDeleteIncidentMessage(message)}
            />
          ))}
        </MessagesContainer>
      )}
      <ModalPortal
        isOpen={modalIsOpen}
        closeModal={() => setModalIsOpen(false)}
        overflow="visible"
        zIndex={131}
        exitButton={false}
        header="Add Message"
      >
        <IncidentMessageModal
          messages={allMessages}
          closeModal={() => setModalIsOpen(false)}
        />
      </ModalPortal>
      <ModalPortal
        isOpen={!!editIncidentMessage}
        closeModal={() => setEditIncidentMessage(null)}
        overflow="visible"
        zIndex={131}
        exitButton={false}
        header="Edit Message"
      >
        <IncidentMessageModal
          messages={allMessages}
          incidentMessage={editIncidentMessage ?? undefined}
          closeModal={() => setEditIncidentMessage(null)}
        />
      </ModalPortal>

      <ModalPortal
        isOpen={!!deleteIncidentMessage}
        closeModal={() => setDeleteIncidentMessage(null)}
        overflow="visible"
        zIndex={131}
        exitButton={false}
        header="Delete Message"
      >
        <DeleteIncidentMessageModal
          header="Delete Message"
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
          incidentMessage={deleteIncidentMessage!}
          closeModal={() => setDeleteIncidentMessage(null)}
        />
      </ModalPortal>
    </>
  );
};

const MessagesContainer = styled.div`
  display: grid;
  gap: 8px;

  @media (min-width: 1800px) {
    grid-template-columns: repeat(auto-fit, minmax(320px, 420px));
    gap: 32px;
  }

  @media (min-width: 1400px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 32px;
  }

  @media (min-width: 800px) {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
`;
